import { useState, useEffect, useRef } from "react";
import { apiFetch } from "../api";
import { trackEvent } from "../analytics/posthog";
import s from "./LoginPage.module.css";

export default function LoginPage({ onSuccess, onBack, onSignup }) {
  const [step, setStep] = useState("email"); // "email" | "otp"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpRef = useRef(null);

  // Countdown for resend button
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Auto-focus OTP input when entering step 2
  useEffect(() => {
    if (step === "otp" && otpRef.current) {
      otpRef.current.focus();
    }
  }, [step]);

  const handleRequestOtp = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await apiFetch("/api/v1/auth/sign-in/request-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Something went wrong");
      }

      setStep("otp");
      setCode("");
      setResendCooldown(30);
      trackEvent("login_otp_requested");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (otpCode) => {
    setError("");
    setLoading(true);

    try {
      const res = await apiFetch("/api/v1/auth/sign-in/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: otpCode }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Invalid code");
      }

      const data = await res.json();
      trackEvent("login_success");
      onSuccess(data);
    } catch (err) {
      setError(err.message);
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(val);
    if (val.length === 6 && !loading) {
      handleVerifyOtp(val);
    }
  };

  const handleChangeEmail = () => {
    setStep("email");
    setCode("");
    setError("");
  };

  return (
    <div className={s.page}>
      <div className={s.card}>
        {/* Back */}
        <button className={s.back} onClick={onBack}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        {/* Shield */}
        <div className={s.shieldWrap}>
          <div className={s.shield}>
            <svg className={s.shieldIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 20h20" />
              <path d="M4 20V9l4 3 4-7 4 7 4-3v11" />
            </svg>
          </div>
        </div>

        {step === "email" ? (
          <>
            <h1 className={s.title}>Welcome back</h1>
            <p className={s.subtitle}>Continue your Swedish quest</p>
          </>
        ) : (
          <>
            <h1 className={s.title}>Check your email</h1>
            <p className={s.subtitle}>Enter the 6-digit code we sent you. Check your junk/spam folder if you don't see it.</p>
          </>
        )}

        {/* Divider */}
        <div className={s.divider}>
          <div className={s.dividerLine} />
          <div className={s.dividerDot} />
          <div className={s.dividerLine} />
        </div>

        {step === "email" ? (
          /* ── Step 1: Email ── */
          <form onSubmit={handleRequestOtp}>
            <div className={s.fieldGroup}>
              <div className={s.field}>
                <label className={s.fieldLabel}>Email</label>
                <input
                  className={s.fieldInput}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            {error && (
              <div className={s.error}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                {error}
              </div>
            )}

            <button type="submit" className={s.submitBtn} disabled={loading}>
              {loading ? (
                <span className={s.btnLoading}>
                  <span className={s.btnSpinner} />
                  Sending code...
                </span>
              ) : (
                "Send sign-in code"
              )}
            </button>
          </form>
        ) : (
          /* ── Step 2: OTP ── */
          <div>
            <div className={s.emailDisplay}>{email}</div>

            <div className={s.fieldGroup}>
              <input
                ref={otpRef}
                className={s.otpInput}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength="6"
                value={code}
                onChange={handleOtpChange}
                placeholder="000000"
                disabled={loading}
              />
            </div>

            {error && (
              <div className={s.error}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                {error}
              </div>
            )}

            {loading && (
              <div className={s.otpVerifying}>
                <span className={s.btnSpinner} />
                Verifying...
              </div>
            )}

            <div className={s.otpActions}>
              <button
                className={s.resendBtn}
                onClick={handleRequestOtp}
                disabled={resendCooldown > 0 || loading}
              >
                {resendCooldown > 0
                  ? `Resend code (${resendCooldown}s)`
                  : "Resend code"}
              </button>
              <button
                className={s.changeEmailBtn}
                onClick={handleChangeEmail}
                disabled={loading}
              >
                Change email
              </button>
            </div>
          </div>
        )}

        {/* Sign up link */}
        <p className={s.signupLink}>
          Don't have an account?{" "}
          <button type="button" className={s.signupBtn} onClick={onSignup}>
            Sign up
          </button>
        </p>

        {/* Quest badges */}
        <div className={s.badges}>
          <div className={s.badge}>
            <div className={`${s.badgeIcon} ${s.badgeIconAmber}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20V10" />
                <path d="M18 20V4" />
                <path d="M6 20v-4" />
              </svg>
            </div>
            <span className={s.badgeLabel}>Quests</span>
          </div>

          <div className={s.badge}>
            <div className={`${s.badgeIcon} ${s.badgeIconIndigo}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
              </svg>
            </div>
            <span className={s.badgeLabel}>Stories</span>
          </div>

          <div className={s.badge}>
            <div className={`${s.badgeIcon} ${s.badgeIconGold}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </div>
            <span className={s.badgeLabel}>XP Stars</span>
          </div>
        </div>
      </div>
    </div>
  );
}
